import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { Modal } from 'bootstrap';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { QuestionService } from 'src/app/common/question-dialog/question-dialog.service';

import { Data2Service } from '../../common/data2.service';
import { DateService } from '../../common/date.service';
import { InfoboxService } from '../../common/infobox/infobox.service';
import {
	BankCard,
	BankCardOwner,
	BankCardOwnerPosition,
	BankDepartments,
	BankOwnersTypes,
	BankOwnersTypesOther,
	BankVehicleKind,
	BankVehicles,
	OwnerKind,
} from '../bank.model';
import { VehicleKind } from '@/vehicles/vehicle.model';
import { Driver } from '@/drivers/driver.model';
import { Users } from '@/users/users.model';

@Component({
	selector: 'app-bank-card-owners',
	templateUrl: './bank-card-owners.component.html',
	styleUrls: ['./bank-card-owners.component.css'],
})
export class BankCardOwnersComponent implements OnChanges {
	@Input() ownersTypes: BankOwnersTypes[] = [];
	@Input() users: Users[] = [];
	@Input() drivers: Driver[] = [];
	@Input() vehicles: BankVehicles[] = [];
	@Input() departments: BankDepartments[] = [];
	@Input() ownersOther: BankOwnersTypesOther[] = [];
	@Input() card: BankCard;
	@Output() cardChange = new EventEmitter();
	owners: BankCardOwner[] = [];
	ownersIds: { id: string; name: string }[] = [];
	data: BankCardOwner = {
		id: null,
		bankCard: null,
		ownerType: null,
		ownerId: null,
		dateFrom: null,
		dateTo: null,
	};
	tableRows: { id: string; name: string }[] = [
		{ id: 'ownerTypeName', name: 'Rodzaj' },
		{ id: 'ownerName', name: 'Właściciel' },
		{ id: 'dateFrom', name: 'Data wydania' },
		{ id: 'dateTo', name: 'Data zwrotu' },
	];
	isItemBefore = false;
	isItemAfter = false;
	private questionsetDeleteSubscription;

	constructor(
		private readonly api: Data2Service,
		private readonly date: DateService,
		private readonly infobox: InfoboxService,
		private readonly loader: LoaderService,
		private readonly question: QuestionService,
	) {}

	ngOnChanges(): void {
		if (
			!this.card ||
			!this.card.number ||
			!this.ownersTypes ||
			!this.ownersTypes.length ||
			!this.users ||
			!this.users.length ||
			!this.drivers ||
			!this.drivers.length ||
			!this.vehicles ||
			!this.vehicles.length
		) {
			return;
		}
		this.data.bankCard = this.card.id;
		this.loadData();
	}

	private loadData(): void {
		this.loader.show();
		this.api.get(`/bankCardsOwners/?bankCard=${this.card.id}`, (result, error) => {
			result.data.forEach((element) => {
				const ownerTypeName = this.ownersTypes.find((obj) => obj.id == element.ownerType);
				element.ownerTypeName = ownerTypeName ? ownerTypeName.name : '??';
				element.ownerName = this.getOwnerName(element.ownerType, element.ownerId);
			});
			this.owners = result.data;
			this.loader.hide();
		});
	}

	private getOwnerName(type: OwnerKind, id: string): string {
		switch (type) {
			case OwnerKind.EMPLOYEE:
				return this.users.find((obj) => obj.id === id)?.name;
			case OwnerKind.DRIVER:
				return this.drivers.find((obj) => obj.id === id)?.name;
			case OwnerKind.COACH:
			case OwnerKind.TIR:
				return this.vehicles.find((obj) => obj.id === id)?.name;
			case OwnerKind.DEMPARTMENT:
				return this.departments.find((obj) => obj.id === id)?.name;
			case OwnerKind.OTHER:
				return this.ownersOther.find((obj) => obj.id === id)?.name;
			default:
				const neverType: never = type;
				throw new Error(`There is no type like ${neverType}`);
		}
	}

	selectOwner(owner: BankCardOwnerPosition): void {
		this.data = this.owners.find((obj) => obj.id === owner.id);
		this.ownerTypeSelected();
		const positionInArray = this.owners.indexOf(this.data);
		this.isItemBefore = positionInArray === 0 ? false : true;
		this.isItemAfter = positionInArray === this.owners.length - 1 ? false : true;
		new Modal(document.getElementById('ownerSetModal')).show();
	}

	showAdd(): void {
		this.data = {
			id: null,
			bankCard: this.card.id,
			ownerType: null,
			ownerId: null,
			dateFrom: this.date.now().date,
			dateTo: null,
		};
		this.isItemAfter = false;
		this.isItemBefore = this.owners.length > 0 ? true : false;
		if (this.isItemBefore) {
			const lastOwner = this.owners[this.owners.length - 1];
			if (!lastOwner.dateTo) {
				this.infobox.error('Uzupełnij datę zwrotu ostatniego właściciela');
				return;
			}
			this.data.dateFrom = this.date.addDays(1, lastOwner.dateTo);
		}
		new Modal(document.getElementById('ownerSetModal')).show();
	}

	dialogClose(): void {
		const modal = Modal.getInstance(document.getElementById('ownerSetModal'));
		if (!modal) {
			return;
		}
		modal.hide();
	}

	submit(): void {
		if (this.data.dateTo && this.data.dateTo <= this.data.dateFrom) {
			this.infobox.error('Data zwrotu musi być większa od daty wydania');
			return;
		}
		if (this.data.id) {
			this.update();
			return;
		}
		this.add();
	}

	private add(): void {
		this.api.post('/bankCardsOwners/', this.data, (result, error) => {
			this.data.id = result.data;
			this.loadData();
			this.dialogClose();
		});
	}

	private update(): void {
		if (!this.data.dateTo) this.data.dateTo = null;
		this.api.put(`/bankCardsOwners/${this.data.id}/`, this.data, (result, error) => {
			this.loadData();
			this.dialogClose();
		});
	}

	ownerTypeSelected(): void {
		switch (this.data.ownerType) {
			case OwnerKind.EMPLOYEE:
				this.ownersIds = this.users;
				break;
			case OwnerKind.DRIVER:
				this.ownersIds = this.drivers;
				break;
			case OwnerKind.COACH:
				this.ownersIds = this.vehicles.filter((obj) => BankVehicleKind.indexOf(obj.type.id));
				break;
			case OwnerKind.DEMPARTMENT:
				this.ownersIds = this.departments;
				break;
			case OwnerKind.TIR:
				this.ownersIds = this.vehicles.filter((obj) => obj.type.id === VehicleKind.TIR);
				break;
			case OwnerKind.OTHER:
				this.ownersIds = this.ownersOther;
				break;
		}
	}

	deleteOpen(): void {
		this.dialogClose();
		this.question.ask('Czy na pewno usunąć?', 'deleteAsk');
		this.questionsetDeleteSubscription = this.question.status.subscribe((result) => {
			if (result.dialogId != 'deleteAsk') {
				return;
			}
			this.questionsetDeleteSubscription.unsubscribe();
			if (!result.result) {
				return;
			}
			this.api.del(`/bankCardsOwners/${this.data.id}/`, (result, error) => {
				if (error) {
					return;
				}
				this.loadData();
			});
		});
	}
}
